// Currency utilities for localized price display
// Rates are relative to EUR (1 EUR = X unit). Refreshed live at boot; fallback to sensible defaults.
export const CURRENCY_RATES = {
  EUR: 1.0,
  USD: 1.08,
  CNY: 7.85,
  XOF: 655.957,
  MAD: 10.85,
};

let ratesUpdatedAt = null;

/** Fetch live EUR-based rates and update in-memory table. Cached 12h in localStorage. */
export async function refreshRates() {
  try {
    const cached = JSON.parse(localStorage.getItem("negosino_rates") || "null");
    const now = Date.now();
    if (cached && cached.at && now - cached.at < 12 * 3600 * 1000) {
      Object.assign(CURRENCY_RATES, cached.rates);
      ratesUpdatedAt = cached.at;
      return CURRENCY_RATES;
    }
    const r = await fetch("https://open.er-api.com/v6/latest/EUR");
    if (!r.ok) throw new Error("rates fetch failed");
    const data = await r.json();
    if (data && data.rates) {
      const kept = {};
      for (const k of Object.keys(CURRENCY_RATES)) {
        if (data.rates[k]) { CURRENCY_RATES[k] = data.rates[k]; kept[k] = data.rates[k]; }
      }
      ratesUpdatedAt = now;
      localStorage.setItem("negosino_rates", JSON.stringify({ rates: kept, at: now }));
    }
  } catch (e) {
    // Silently keep fallback rates
  }
  return CURRENCY_RATES;
}

export function ratesLastUpdate() { return ratesUpdatedAt; }

// West-African CFA franc zone timezones (auto-select XOF for these visitors)
const XOF_TIMEZONES = new Set([
  "Africa/Abidjan", "Africa/Bamako", "Africa/Bissau", "Africa/Cotonou",
  "Africa/Dakar", "Africa/Lome", "Africa/Niamey", "Africa/Ouagadougou",
  "Africa/Conakry", "Africa/Nouakchott",
]);

export function autoDetectCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (XOF_TIMEZONES.has(tz)) return "XOF";
  } catch {}
  return null;
}

// Default display currency per language (used when user hasn't picked one)
export const LANG_CURRENCY = {
  fr: "EUR",
  en: "USD",
  zh: "CNY",
  ar: "USD",
  es: "USD",
};

export const CURRENCY_META = {
  EUR: { label: "Euro", symbol: "€", flag: "🇪🇺" },
  USD: { label: "US Dollar", symbol: "$", flag: "🇺🇸" },
  CNY: { label: "人民币", symbol: "¥", flag: "🇨🇳" },
  XOF: { label: "Franc CFA", symbol: "FCFA", flag: "🇸🇳" },
};

const SYMBOLS = { EUR: "€", USD: "$", CNY: "¥", XOF: "FCFA", MAD: "MAD" };

export function convertPrice(amount, fromCurrency, toCurrency) {
  if (fromCurrency === toCurrency) return amount;
  const rFrom = CURRENCY_RATES[fromCurrency] ?? 1;
  const rTo = CURRENCY_RATES[toCurrency] ?? 1;
  const inEur = amount / rFrom;
  return inEur * rTo;
}

export function formatPrice(amount, currency, locale = "fr-FR") {
  const n = currency === "XOF" ? Math.round(amount / 100) * 100 : Math.round(amount);
  const formatted = n.toLocaleString(locale);
  const sym = SYMBOLS[currency] || currency;
  if (currency === "USD" || currency === "CNY") return `${sym}${formatted}`;
  return `${formatted} ${sym}`;
}

export function displayPrice(amount, baseCurrency, lang, userCurrency) {
  const target = userCurrency || LANG_CURRENCY[lang] || baseCurrency;
  const converted = convertPrice(amount, baseCurrency, target);
  const locale = { fr: "fr-FR", en: "en-US", zh: "zh-CN", ar: "ar", es: "es" }[lang] || "fr-FR";
  return {
    text: formatPrice(converted, target, locale),
    currency: target,
    base: formatPrice(amount, baseCurrency, locale),
  };
}
